import { useState, useEffect, useCallback } from 'react';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 2 * 60 * 1000;        // 2 minutes warning

export const useUserActivity = ({ disabled = false } = {}) => {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);

  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  const handleTimeout = useCallback(() => {
    if (disabled) return;
    console.log('HIPAA: Forcing logout due to session timeout');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('csrfToken');
    window.location.href = '/hipaa-session-expiry';
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    const interval = setInterval(() => {
      const now = Date.now();
      const inactiveTime = now - lastActivity;

      if (inactiveTime > INACTIVITY_TIMEOUT) {
        handleTimeout();
      } else if (inactiveTime > INACTIVITY_TIMEOUT - WARNING_TIME) {
        if (!showWarning) {
          setShowWarning(true);
        }
      }
    }, 30000);

    return () => {
      events.forEach(event => document.removeEventListener(event, resetTimer));
      clearInterval(interval);
    };
  }, [lastActivity, resetTimer, showWarning, disabled, handleTimeout]);

  const extendSession = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  const timeRemaining = INACTIVITY_TIMEOUT - (Date.now() - lastActivity);
  const minutesRemaining = Math.max(0, Math.floor(timeRemaining / 60000));
  const secondsRemaining = Math.max(0, Math.floor((timeRemaining % 60000) / 1000));

  return {
    showWarning,
    extendSession,
    timeRemaining,
    minutesRemaining,
    secondsRemaining
  };
};

export default useUserActivity;
